import { ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { FetcherBrandTraceRow, rowId } from "../helpers/fetcherCatalogue";
import { formatDisplayDate } from "../helpers/formatters";

interface Props {
  brand: FetcherBrandTraceRow;
}

// the mockup's .pgk / .pgv
const LABEL_SX = {
  fontSize: "11px",
  letterSpacing: "0.4px",
  textTransform: "uppercase" as const,
  color: "#9A9AA4",
  fontWeight: 700,
};
const VALUE_SX = { fontSize: "13.5px", mt: "3px" };

const MONO_SX = {
  ...VALUE_SX,
  fontFamily: "monospace",
  fontSize: "12.5px",
  color: "#6E6E78",
};

/** The mockup's .webchip - the interface pill. */
const InterfacePill = ({ label }: { label: string }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      backgroundColor: "secondary.main",
      color: "secondary.contrastText",
      borderRadius: "14px",
      padding: "3px 11px",
      fontSize: "11.5px",
      fontWeight: 500,
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </Box>
);

/** The mockup's .st - the brand account's own run gate. */
const StatusPill = ({ isActive }: { isActive: boolean }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "5px",
      padding: "3px 10px",
      borderRadius: "4px",
      fontSize: "12px",
      fontWeight: 500,
      whiteSpace: "nowrap",
      border: "1px solid",
      ...(isActive
        ? {
            backgroundColor: "#E7F5EC",
            color: "#1E8E4E",
            borderColor: "#BFE3CC",
          }
        : {
            backgroundColor: "#FBE9E9",
            color: "#C0392B",
            borderColor: "#F0C9C4",
          }),
    }}
  >
    {isActive ? "✓ Active" : "⊗ Inactive"}
  </Box>
);

/** A value the brand does not carry, shown as a muted N/A chip. */
const NotSet = () => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      backgroundColor: "#F0F0F3",
      color: "#9A9AA4",
      borderRadius: "4px",
      padding: "2px 8px",
      fontFamily: "monospace",
      fontSize: "11.5px",
    }}
  >
    N/A
  </Box>
);

/** The brand's own row - the run gates plus how the runner identifies it. */
const BrandTraceSummary = ({ brand }: Props) => {
  const mono = (value: string | null) =>
    value ? (
      <Typography sx={MONO_SX}>{value}</Typography>
    ) : (
      <Box sx={VALUE_SX}>
        <NotSet />
      </Box>
    );

  const cells: { label: string; value: ReactNode }[] = [
    {
      label: "Brand",
      value: (
        <Typography sx={VALUE_SX}>
          <b>{brand.brand_name}</b>{" "}
          <Box component="span" sx={{ color: "#9A9AA4", fontSize: "0.88em" }}>
            ({rowId(brand.brand_id)})
          </Box>
        </Typography>
      ),
    },
    {
      label: "Interface",
      value: (
        <Box sx={VALUE_SX}>
          {brand.interface_name ? (
            <InterfacePill label={brand.interface_name} />
          ) : (
            <NotSet />
          )}
        </Box>
      ),
    },
    {
      label: "Status",
      value: (
        <Box sx={VALUE_SX}>
          <StatusPill isActive={Number(brand.account_active) === 1} />
        </Box>
      ),
    },
    // a vault reference, never the secret itself
    { label: "Keeper", value: mono(brand.keeper_id) },
    { label: "Username", value: mono(brand.username) },
    { label: "Dynamic var", value: mono(brand.dynamic_var) },
    {
      label: "Run from",
      value: brand.run_from_date ? (
        <Typography sx={MONO_SX}>
          {formatDisplayDate(brand.run_from_date)}
        </Typography>
      ) : (
        <Box sx={VALUE_SX}>
          <NotSet />
        </Box>
      ),
    },
    {
      label: "Connections",
      value: (
        <Typography sx={VALUE_SX}>{brand.connection_count ?? 0}</Typography>
      ),
    },
  ];

  return (
    <Stack
      direction="row"
      flexWrap="wrap"
      sx={{
        gap: "20px",
        padding: "16px 18px",
        borderBottom: "1px solid #E7E7EC",
        backgroundColor: "#FBFAFD",
      }}
    >
      {cells.map((cell) => (
        <Box key={cell.label}>
          <Typography sx={LABEL_SX}>{cell.label}</Typography>
          {cell.value}
        </Box>
      ))}
    </Stack>
  );
};

export default BrandTraceSummary;
